import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MapCustomObjectsActions } from '@states/map-custom-objects/map-custom-objects.action-types';
import { catchError, finalize, map, Observable, of, startWith, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MapService } from '../../services/map.service';
import { MapActions } from '@states/map/map.action-types';
import { Store } from '@ngrx/store';

@Injectable()
export class MapEffects {
  public getAllObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MapActions.getAllObjects),
      switchMap(() => {
        return [
          MapActions.sendGetAllObjectsRequest(),
        ];
      }),
    ),
  );

  public sendGetAllObjectsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MapActions.sendGetAllObjectsRequest),
      switchMap(() => {
        return this.mapService.getAllObjects().pipe(
          switchMap((objects) => {
            return [
              MapActions.getAllObjectsSuccess({ objects }),
            ];
          }),
          catchError(() => {
            return [
              MapActions.getAllObjectsFail(),
            ];
          }),
        );
      }),
    ),
  );

  public getMarkerPicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MapActions.getMarkerPicture),
      switchMap(({id}) => {
        return [
          MapActions.setIsLoading({ isLoading: true }),
          MapActions.sendMarkerPictureRequest({id}),
        ];
      }),
    ),
  );

  public sendMarkerPictureRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MapActions.sendMarkerPictureRequest),
      switchMap(({ id }) =>
        this.mapService.getMarkerPicture(id).pipe(
          map((array) => array[0].content.hugeJpeg as string),
          switchMap((imageUrl) =>
            this.preloadImage(imageUrl).pipe(
              map(() => MapActions.getMarkerPictureSuccess({ imageUrl })),
              catchError(() => of(MapActions.getMarkerPictureFail())),
              finalize(() =>
                this.store$.dispatch(
                  MapActions.setIsLoading({ isLoading: false })
                )
              ),
              startWith(
                MapActions.setIsLoading({ isLoading: true })
              )
            )
          ),
          catchError((err) => {
            return [MapActions.setIsLoading({ isLoading: false })]
          }),
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private mapService: MapService,
    private translateService: TranslateService,
    private store$: Store,
  ) {}

  private preloadImage(url: string): Observable<string> {
    return new Observable<string>((observer) => {
      const img = new Image();
      img.onload = () => {
        observer.next(url);
        observer.complete();
      };
      img.onerror = (err) => {
        observer.error(err);
      };
      img.src = url;
      return () => {
        img.onload = null;
        img.onerror = null;
        img.src = '';
      };
    });
  }
}
